import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Language, useLanguage } from './LanguageContext';
import { saveContent, fetchContent } from '../api/contentApi';
import { updateNestedContent, getNestedValue, mergeContentEdits } from '../utils/contentUtils';
import { isAuthenticated, signOut } from '../utils/supabase';
import LoginModal from '../components/auth/LoginModal';

// Environment variables check
const ENABLE_API = process.env.REACT_APP_ENABLE_API === 'true';
const IS_STAGING = process.env.REACT_APP_ENVIRONMENT === 'staging';

interface EditModeContextType {
    isEditMode: boolean;
    toggleEditMode: () => void;
    saveChanges: () => Promise<void>;
    editedContent: Record<Language, Record<string, any>>;
    updateEditedContent: (key: string, value: any) => void;
    getEditedValue: (key: string, defaultValue: string) => string;
    setEditModeEnabled: (enabled: boolean) => void;
    isSaving: boolean;
    isAuthenticated: boolean;
    logout: () => Promise<void>;
}

const EditModeContext = createContext<EditModeContextType | undefined>(undefined);

interface EditModeProviderProps {
    children: ReactNode;
}

function EditModeProvider({ children }: EditModeProviderProps) {
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedContent, setEditedContent] = useState<Record<Language, Record<string, any>>>({
        en: {},
        de: {},
        zh: {},
        fr: {},
    });
    const [isSaving, setIsSaving] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [userIsAuthenticated, setUserIsAuthenticated] = useState(false);
    const [pendingEditMode, setPendingEditMode] = useState(false);
    const location = useLocation();
    const { language } = useLanguage();

    // Check authentication status on mount
    useEffect(() => {
        const checkAuth = async () => {
            if (IS_STAGING) {
                const authStatus = await isAuthenticated();
                setUserIsAuthenticated(authStatus);
            }
        };
        
        checkAuth();
    }, []);

    // Check for edit mode in URL on mount and when URL changes
    useEffect(() => {
        if (!ENABLE_API) {
            // Never enable edit mode in production
            setIsEditMode(false);
            return;
        }

        const searchParams = new URLSearchParams(location.search);
        const editParam = searchParams.get('edit');
        
        // If we're in staging and trying to enable edit mode, check authentication
        if (IS_STAGING && editParam === 'true') {
            if (userIsAuthenticated) {
                setIsEditMode(true);
            } else {
                // Don't set edit mode yet, show login instead
                setShowLoginModal(true);
                setPendingEditMode(true);
                return;
            }
        } else {
            setIsEditMode(editParam === 'true');
        }

        // Reset edited content when exiting edit mode
        if (editParam !== 'true') {
            setEditedContent({
                en: {},
                de: {},
                zh: {},
                fr: {},
            });
        }
    }, [location.search, userIsAuthenticated]);

    const toggleEditMode = () => {
        // Don't allow edit mode in production
        if (!ENABLE_API) {
            console.warn('Edit mode is disabled in production');
            return;
        }

        const newEditMode = !isEditMode;

        // If we're in staging and trying to enable edit mode, check authentication
        if (IS_STAGING && newEditMode) {
            if (!userIsAuthenticated) {
                setShowLoginModal(true);
                setPendingEditMode(true);
                return;
            }
        }

        setIsEditMode(newEditMode);

        // Update URL with edit parameter
        const searchParams = new URLSearchParams(location.search);
        if (newEditMode) {
            searchParams.set('edit', 'true');
        } else {
            searchParams.delete('edit');
        }

        const newUrl = `${location.pathname}?${searchParams.toString()}`;
        window.history.pushState({}, '', newUrl);
    };

    // Explicitly set edit mode state (used by footer button)
    const setEditModeEnabled = (enabled: boolean) => {
        // Don't allow edit mode in production
        if (!ENABLE_API && enabled) {
            console.warn('Edit mode is disabled in production');
            return;
        }

        // If we're in staging and trying to enable edit mode, check authentication
        if (IS_STAGING && enabled) {
            if (!userIsAuthenticated) {
                setShowLoginModal(true);
                setPendingEditMode(true);
                return;
            }
        }

        setIsEditMode(enabled);
    };

    const updateEditedContent = (key: string, value: any) => {
        setEditedContent((prev) => ({
            ...prev,
            [language]: {
                ...prev[language],
                [key]: value,
            },
        }));
    };

    const getEditedValue = (key: string, defaultValue: string): string => {
        return editedContent[language]?.[key] || defaultValue;
    };

    const handleLoginSuccess = () => {
        setUserIsAuthenticated(true);
        setShowLoginModal(false);
        
        // If we had a pending edit mode request, enable it now
        if (pendingEditMode) {
            setIsEditMode(true);
            setPendingEditMode(false);
            
            // Update URL with edit parameter
            const searchParams = new URLSearchParams(location.search);
            searchParams.set('edit', 'true');
            const newUrl = `${location.pathname}?${searchParams.toString()}`;
            window.history.pushState({}, '', newUrl);
        }
    };

    const logout = async () => {
        await signOut();
        setUserIsAuthenticated(false);
        
        // If in edit mode, exit it
        if (isEditMode) {
            setIsEditMode(false);
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('edit');
            const newUrl = `${location.pathname}?${searchParams.toString()}`;
            window.history.pushState({}, '', newUrl);
        }
    };

    const saveChanges = async (): Promise<void> => {
        // In staging, verify authentication before saving
        if (IS_STAGING && !userIsAuthenticated) {
            console.warn('Authentication required to save changes');
            setShowLoginModal(true);
            return;
        }

        setIsSaving(true);

        try {
            // Get all edited content for the current language
            const currentLanguageContent = editedContent[language] || {};

            if (Object.keys(currentLanguageContent).length === 0) {
                console.log('No changes to save');
                return;
            }

            // Log what we're about to save
            console.log('=== SAVING CONTENT ===');
            console.log(`Language: ${language.toUpperCase()}`);
            console.log('Content changes:', currentLanguageContent);

            // Group edits by page
            const pageContentMap: Record<string, string[]> = {};
            
            Object.keys(currentLanguageContent).forEach((key) => {
                const page = key.split('.')[0];
                if (!pageContentMap[page]) {
                    pageContentMap[page] = [];
                }
                pageContentMap[page].push(key);
            });

            // For each page, fetch existing content and merge with edits
            for (const [page, keys] of Object.entries(pageContentMap)) {
                console.log(`Preparing changes for page: ${page}`);

                try {
                    // Fetch current content from API or fallback
                    const existingContent = await fetchContent(page, language);

                    if (!existingContent) {
                        console.error(`Failed to fetch existing content for ${page}`);
                        continue;
                    }

                    // Extract edits for this page
                    const pageEdits: Record<string, any> = {};
                    keys.forEach(key => {
                        // Remove page prefix to get the relative path
                        const relativePath = key.substring(page.length + 1);
                        pageEdits[relativePath] = currentLanguageContent[key];
                    });

                    // Merge existing content with edits
                    const updatedContent = mergeContentEdits(existingContent, pageEdits);

                    // Save to the API
                    const success = await saveContent(page, language, updatedContent);

                    if (success) {
                        console.log(`Successfully saved ${page} content for ${language}`);
                    } else {
                        console.error(`Failed to save ${page} content for ${language}`);
                    }
                } catch (error) {
                    console.error(`Error saving ${page} content:`, error);
                }
            }

            console.log('=== CONTENT SAVE COMPLETE ===');
        } catch (error) {
            console.error('Error in saveChanges:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <EditModeContext.Provider
            value={{
                isEditMode,
                toggleEditMode,
                saveChanges,
                editedContent,
                updateEditedContent,
                getEditedValue,
                setEditModeEnabled,
                isSaving,
                isAuthenticated: userIsAuthenticated,
                logout,
            }}
        >
            {children}
            {showLoginModal && (
                <LoginModal 
                    onClose={() => {
                        setShowLoginModal(false);
                        setPendingEditMode(false);
                    }}
                    onSuccess={handleLoginSuccess}
                />
            )}
        </EditModeContext.Provider>
    );
}

function useEditMode() {
    const context = useContext(EditModeContext);
    if (context === undefined) {
        throw new Error('useEditMode must be used within an EditModeProvider');
    }
    return context;
}

export { EditModeProvider, useEditMode }; 